import { useEffect, useRef } from 'react';
import {
  toMarketUpdated,
  toOrderBookUpdated,
  toOutcomeNew,
  toOutcomeUpdated,
  toPriceUpdated,
  toTradeStreamed,
  type MarketUpdated,
  type OrderBookUpdated,
  type OutcomeNew,
  type OutcomeUpdated,
  type PriceUpdated,
  type TradeStreamed,
  type WsMarketChannelEvent,
} from '@sunflower-games/predictions-realtime-contracts';
import { useRealtimeManager } from '../realtime-manager-ctx';

export interface MarketRealtimeHandlers {
  onMarketUpdated?: (e: MarketUpdated) => void;
  onOutcomeNew?: (e: OutcomeNew) => void;
  onOutcomeUpdated?: (e: OutcomeUpdated) => void;
  onPriceUpdated?: (e: PriceUpdated) => void;
  onOrderBookUpdated?: (e: OrderBookUpdated) => void;
  onTradeStreamed?: (e: TradeStreamed) => void;
}

/**
 * Subscribe to live updates for a single market. Resolves the Plaee channel
 * from the internal market id + `plaeeMarketId` and routes by `type` to the
 * canonical handlers. Cleans up + ref-counts via the {@link RealtimeManager}.
 */
export function useMarketRealtime(
  internalMarketId: string | null | undefined,
  plaeeMarketId: string | null | undefined,
  handlers: MarketRealtimeHandlers,
): void {
  const manager = useRealtimeManager();
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!internalMarketId || !plaeeMarketId) return;
    return manager.subscribeMarket(plaeeMarketId, (raw) => {
      const msg = raw as WsMarketChannelEvent;
      const h = handlersRef.current;
      switch (msg.type) {
        case 'market_updated':
          h.onMarketUpdated?.(toMarketUpdated(internalMarketId, msg));
          return;
        case 'outcome_new':
          h.onOutcomeNew?.(toOutcomeNew(internalMarketId, msg));
          return;
        case 'outcome_updated':
          h.onOutcomeUpdated?.(toOutcomeUpdated(internalMarketId, msg));
          return;
        case 'price_updated':
          h.onPriceUpdated?.(toPriceUpdated(internalMarketId, msg));
          return;
        case 'orderbook_updated':
          h.onOrderBookUpdated?.(toOrderBookUpdated(internalMarketId, msg));
          return;
        case 'trade_streamed':
          h.onTradeStreamed?.(toTradeStreamed(internalMarketId, msg));
          return;
      }
    });
  }, [manager, internalMarketId, plaeeMarketId]);
}
